import {Component, Injector, OnInit} from '@angular/core';
import {Parent} from "../../models/parent";
import {AthleteService} from "../../services/athlete/athlete.service";
import {ActivatedRoute, Router} from "@angular/router";
import {BaseComponent} from "../base/base.component";
import {ParentService} from "../../services/parent/parent.service";
import * as moment from 'moment';
import Swal from "sweetalert2";
import {User} from "../../models/user";
import {UserService} from "../../services/user/user.service";
import {Athlete} from "../../models/athlete";
import {
    fiscalCodeBirthDateMismatch, minBornIsoDate, normalizeFiscalCode, normalizeSpaces, todayIsoDate,
    validateBornDate, validateEmail, validateFiscalCode, validateName, validatePhone, validateRequiredText, validateZip
} from "../../resources/person-validation";
import {loadItalianProvinces, Province, residenceFromLookup} from "../../resources/provinces";
import {AddressLookupResult} from "../../services/address-lookup/address-lookup.service";

type AthleteField = 'image' | 'firstName' | 'lastName' | 'bornDate' | 'bornCity' | 'fiscalCode' | 'citizenshipType'
    | 'address' | 'city' | 'province' | 'phone' | 'email';
type ParentField = 'firstName' | 'lastName' | 'fiscalCode' | 'bornDate' | 'bornCity' | 'address' | 'city' | 'zip';
type TextField = 'firstName' | 'lastName' | 'bornCity' | 'address' | 'city' | 'email' | 'zip';

const ATHLETE_FIELDS: AthleteField[] = ['image', 'firstName', 'lastName', 'bornDate', 'bornCity', 'fiscalCode', 'citizenshipType',
    'address', 'city', 'province', 'phone', 'email'];
const PARENT_FIELDS: ParentField[] = ['firstName', 'lastName', 'fiscalCode', 'bornDate', 'bornCity', 'address', 'city', 'zip'];
const MAX_PARENTS = 2;

@Component({
    selector: 'app-edit-user',
    templateUrl: './edit-user.component.html'
})
export class EditUserComponent extends BaseComponent implements OnInit {
    parents: Parent[] = [];

    athleteId: string | undefined = '';
    athlete: Athlete = new Athlete();
    user: User = new User();
    isAdult: boolean = false;
    provinces: Province[] = [];

    readonly maxInputDate: string = todayIsoDate();
    readonly minInputDate: string = minBornIsoDate();
    readonly maxParents = MAX_PARENTS;
    editParent: boolean;
    editAthlete: boolean;
    newParent: Parent = new Parent();

    loading = true;
    saving = false;
    uploadingAthleteImage = false;
    uploadingParentImageId: string | undefined;
    savingParentId: string | undefined;
    showAddParent = false;
    addingParent = false;

    citizenshipTypes = [
        {id: 'italiano', name: 'Italiano'},
        {id: 'europeo_non_ita', name: 'Europeo non ITA'},
        {id: 'extra_comunitario_eu', name: 'Extra comunitario EU'}
    ];

    /** Campi "toccati" (blur) e form inviati: gli errori si mostrano solo dopo uno dei due. */
    /** Genitori con il form di modifica aperto (chiave = parentKey) e copia dei dati per "Annulla". */
    private expandedParents = new Set<string>();
    private parentSnapshots = new Map<string, Parent>();

    private touched = new Set<string>();
    private submitted = new Set<string>();

    constructor(public athleteService: AthleteService, public parentService: ParentService, public userService: UserService, public route: ActivatedRoute, public router: Router, public serviceInjector: Injector) {
        super(serviceInjector);

        this.editParent = this.route.snapshot.data['editParent'];
        this.editAthlete = this.route.snapshot.data['editAthlete'];
    }

    async ngOnInit() {
        if (this.route.snapshot.params['athleteId'] && this.isUUID(this.route.snapshot.params['athleteId'])) {
            this.athleteId = this.route.snapshot.params['athleteId'];
        } else {
            this.router.navigate(['/users/dashboard']);
            return;
        }

        this.loadProvinces();

        try {
            if (this.editParent) {
                await this.loadParents();
            }
            if (this.editAthlete) {
                const [athlete, currentUser, parents] = await Promise.all([
                    this.athleteService.getAthleteById(this.athleteId!),
                    this.userService.getCurrentUser(),
                    this.athleteService.getAthleteParents(this.athleteId!),
                ]);
                this.athlete = athlete;
                this.athlete.bornDate = this.athlete.bornDate ? this.formatDateInput(this.athlete.bornDate) : '';
                this.athlete.fiscalCode = normalizeFiscalCode(this.athlete.fiscalCode);
                this.user = currentUser.data;
                this.isAdult = parents.length === 0;
            }
        } catch (e) {
            console.error('error loading edit user: ', e);
        } finally {
            this.loading = false;
        }
    }

    get backRoute(): any[] {
        return ['/users/profile', this.athleteId];
    }

    // ---- Validazione -------------------------------------------------------

    athleteError(field: AthleteField): string | null {
        const a = this.athlete;
        switch (field) {
            case 'image': return a.image ? null : 'Carica una foto riconoscibile dell\'atleta.';
            case 'firstName': return validateName(a.firstName, 'Il nome');
            case 'lastName': return validateName(a.lastName, 'Il cognome');
            case 'bornDate': return validateBornDate(a.bornDate);
            case 'bornCity': return validateRequiredText(a.bornCity, 'Inserisci il luogo di nascita.');
            case 'fiscalCode': return validateFiscalCode(a.fiscalCode);
            case 'citizenshipType': return a.citizenshipType ? null : 'Seleziona la nazionalità.';
            case 'address': return validateRequiredText(a.address, 'Inserisci l\'indirizzo di residenza.', 3);
            case 'city': return validateRequiredText(a.city, 'Inserisci la città di residenza.');
            case 'province': return a.province ? null : 'Seleziona la provincia.';
            case 'phone': return validatePhone(a.phone);
            case 'email': return validateEmail(a.email);
        }
    }

    showAthleteError(field: AthleteField): string | null {
        return this.touched.has(`athlete:${field}`) || this.submitted.has('athlete') ? this.athleteError(field) : null;
    }

    get athleteErrorCount(): number {
        return ATHLETE_FIELDS.filter(field => this.athleteError(field)).length;
    }

    get athleteFiscalCodeMismatch(): boolean {
        return fiscalCodeBirthDateMismatch(this.athlete.fiscalCode, this.athlete.bornDate);
    }

    parentError(parent: Parent, field: ParentField): string | null {
        switch (field) {
            case 'firstName': return validateName(parent.firstName, 'Il nome');
            case 'lastName': return validateName(parent.lastName, 'Il cognome');
            case 'fiscalCode': return validateFiscalCode(parent.fiscalCode);
            case 'bornDate': return validateBornDate(parent.bornDate);
            case 'bornCity': return validateRequiredText(parent.bornCity, 'Inserisci il luogo di nascita.');
            case 'address': return validateRequiredText(parent.address, 'Inserisci l\'indirizzo di residenza.', 3);
            case 'city': return validateRequiredText(parent.city, 'Inserisci la città di residenza.');
            case 'zip': return validateZip(parent.zip);
        }
    }

    showParentError(parent: Parent, key: string, field: ParentField): string | null {
        return this.touched.has(`${key}:${field}`) || this.submitted.has(key) ? this.parentError(parent, field) : null;
    }

    parentErrorCount(parent: Parent): number {
        return PARENT_FIELDS.filter(field => this.parentError(parent, field)).length;
    }

    parentFiscalCodeMismatch(parent: Parent): boolean {
        return fiscalCodeBirthDateMismatch(parent.fiscalCode, parent.bornDate);
    }

    markTouched(key: string): void {
        this.touched.add(key);
    }

    normalizeText(target: Athlete | Parent, field: TextField): void {
        (target as any)[field] = normalizeSpaces((target as any)[field]);
    }

    onFiscalCodeInput(target: Athlete | Parent, value: string): void {
        target.fiscalCode = normalizeFiscalCode(value);
    }

    onAthletePhoneChange(value: string): void {
        this.athlete.phone = value;
        this.markTouched('athlete:phone');
    }

    onAthleteAddressSelected(result: AddressLookupResult): void {
        const residence = residenceFromLookup(result, this.provinces);
        this.athlete.address = residence.address;
        if (residence.city) this.athlete.city = residence.city;
        if (residence.province) this.athlete.province = residence.province;
        ['address', 'city', 'province'].forEach(field => this.markTouched(`athlete:${field}`));
    }

    onParentAddressSelected(parent: Parent, key: string, result: AddressLookupResult): void {
        const residence = residenceFromLookup(result, this.provinces);
        parent.address = residence.address;
        if (residence.city) parent.city = residence.city;
        const zip = (result.address?.postcode || '').trim();
        if (/^\d{5}$/.test(zip)) parent.zip = zip;
        ['address', 'city', 'zip'].forEach(field => this.markTouched(`${key}:${field}`));
    }

    isParentExpanded(key: string): boolean {
        return this.expandedParents.has(key);
    }

    editParentCard(parent: Parent, key: string): void {
        this.parentSnapshots.set(key, {...parent} as Parent);
        this.expandedParents.add(key);
        setTimeout(() => document.getElementById(`${key}-firstName`)?.focus());
    }

    cancelEditParent(parent: Parent, key: string): void {
        const snapshot = this.parentSnapshots.get(key);
        if (snapshot) Object.assign(parent, snapshot);
        this.collapseParent(key);
    }

    private collapseParent(key: string): void {
        this.expandedParents.delete(key);
        this.parentSnapshots.delete(key);
        this.submitted.delete(key);
        [...this.touched].filter(t => t.startsWith(`${key}:`)).forEach(t => this.touched.delete(t));
    }

    parentKey(parent: Parent, index: number): string {
        return `parent-${parent.id ?? index}`;
    }

    // ---- Atleta --------------------------------------------------------------

    async updateAthleteProfilePicture(event: any) {
        const profilePic: File | undefined = event?.target?.files?.[0];
        if (!profilePic) return;

        this.uploadingAthleteImage = true;
        try {
            const formData = new FormData();
            formData.append('file', profilePic);
            const {success, data} = await this.athleteService.updateProfilePicture(formData);
            if (success) {
                this.athlete.image = data;
                this.markTouched('athlete:image');
            } else {
                this.showErrorMessage("Errore nel caricamento dell'immagine, riprova.");
            }
        } catch (e) {
            console.error('error updateProfilepic: ', e);
            this.showErrorMessage("Errore nel caricamento dell'immagine, riprova.");
        } finally {
            this.uploadingAthleteImage = false;
            if (event?.target) event.target.value = '';
        }
    }

    async updateUser() {
        this.submitted.add('athlete');
        if (this.athleteErrorCount > 0) {
            this.focusFirstInvalid('athlete', ATHLETE_FIELDS.find(field => this.athleteError(field)));
            return;
        }

        this.saving = true;
        try {
            this.athlete.bornDate = moment(this.athlete.bornDate).local(true).format('YYYY-MM-DD');
            this.athlete.email = (this.athlete.email || '').trim().toLowerCase();

            const responseAthlete: boolean = await this.athleteService.updateAthlete(this.athlete);
            const responseUser: boolean = this.isAdult ? await this.userService.updateUser(this.user) : true;

            if (responseAthlete && responseUser) {
                Swal.fire({icon: 'success', title: 'Atleta aggiornato con successo'});
                this.router.navigate(this.backRoute);
            } else {
                this.showErrorMessage("Errore durante l'aggiornamento dell'atleta, riprova.");
            }
        } finally {
            this.saving = false;
        }
    }

    // ---- Genitori ------------------------------------------------------------

    async updateProfilePicture(event: any, parent: Parent) {
        const profilePic: File | undefined = event?.target?.files?.[0];
        if (!profilePic || !parent.id) return;

        this.uploadingParentImageId = parent.id;
        try {
            const formData = new FormData();
            formData.append('file', profilePic);
            const {success, data} = await this.parentService.updateProfilePicture(formData);

            const updateParentPicture = new Parent();
            updateParentPicture.id = parent.id;
            updateParentPicture.userId = localStorage.getItem('wisper_userId') ?? '';
            updateParentPicture.image = data;

            if (success && await this.parentService.updateParent(updateParentPicture)) {
                parent.image = data;
            } else {
                this.showErrorMessage("Errore nel caricamento dell'immagine, riprova.");
            }
        } catch (e) {
            console.error('error updateProfilepic: ', e);
            this.showErrorMessage("Errore nel caricamento dell'immagine, riprova.");
        } finally {
            this.uploadingParentImageId = undefined;
            if (event?.target) event.target.value = '';
        }
    }

    async updateParent(parent: Parent, index: number) {
        const key = this.parentKey(parent, index);
        this.submitted.add(key);
        if (this.parentErrorCount(parent) > 0) {
            this.focusFirstInvalid(key, PARENT_FIELDS.find(field => this.parentError(parent, field)));
            return;
        }

        this.savingParentId = parent.id;
        try {
            if (await this.parentService.updateParent(parent)) {
                this.collapseParent(key);
                Swal.fire({icon: 'success', title: 'Genitore aggiornato con successo'});
            } else {
                this.showErrorMessage("Errore nell'aggiornamento del genitore, riprova.");
            }
        } finally {
            this.savingParentId = undefined;
        }
    }

    openAddParent() {
        this.newParent = new Parent();
        this.submitted.delete('new-parent');
        [...this.touched].filter(key => key.startsWith('new-parent:')).forEach(key => this.touched.delete(key));
        this.showAddParent = true;
        setTimeout(() => document.getElementById('new-parent-firstName')?.focus());
    }

    cancelAddParent() {
        this.showAddParent = false;
    }

    async addNewParent() {
        this.submitted.add('new-parent');
        if (this.parentErrorCount(this.newParent) > 0) {
            this.focusFirstInvalid('new-parent', PARENT_FIELDS.find(field => this.parentError(this.newParent, field)));
            return;
        }

        this.addingParent = true;
        try {
            this.newParent.userId = localStorage.getItem('wisper_userId') ?? '';
            if (this.newParent.athlete) {
                this.newParent.athlete.id = this.athleteId;
            }

            if (await this.parentService.addParent(this.newParent)) {
                this.showAddParent = false;
                await this.loadParents();
                Swal.fire({icon: 'success', title: 'Genitore aggiunto con successo!'});
            } else {
                this.showErrorMessage(`Impossibile aggiungere il genitore: l'atleta può avere al massimo ${MAX_PARENTS} genitori associati.`);
            }
        } catch (e) {
            console.error('error adding parent: ', e);
            this.showErrorMessage('Errore durante il salvataggio del genitore, riprova.');
        } finally {
            this.addingParent = false;
        }
    }

    deleteParent(parentId: string | undefined) {
        Swal.fire({
            title: 'Sei sicuro di voler eliminare questo genitore?',
            text: "Questa azione non può essere annullata!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sì, elimina il genitore',
            cancelButtonText: "Annulla l'operazione"
        }).then(async (result) => {
            if (result.isConfirmed) {
                const response = await this.parentService.deleteParent(parentId);
                if (response) {
                    this.parents = this.parents.filter(parent => parent.id !== parentId);
                    Swal.fire('Eliminato!', 'Il genitore è stato eliminato con successo.', 'success');
                } else {
                    Swal.fire('Errore!', "Si è verificato un errore durante l'eliminazione del genitore.", 'error');
                }
            }
        });
    }

    // ---- Supporto ------------------------------------------------------------

    private async loadParents() {
        this.parents = (await this.athleteService.getAthleteParents(this.athleteId!)).map((parent: Parent) => {
            parent.bornDate = parent.bornDate ? this.formatDateInput(parent.bornDate) : '';
            parent.fiscalCode = normalizeFiscalCode(parent.fiscalCode);
            return parent;
        });
        // I genitori con dati mancanti si aprono già in modifica.
        this.expandedParents.clear();
        this.parents.forEach((parent, index) => {
            if (this.parentErrorCount(parent) > 0) this.expandedParents.add(this.parentKey(parent, index));
        });
    }

    private loadProvinces() {
        loadItalianProvinces().then(provinces => this.provinces = provinces);
    }

    private focusFirstInvalid(formKey: string, field: string | undefined) {
        if (!field) return;
        setTimeout(() => {
            const target = document.querySelector<HTMLElement>(`[data-field="${formKey}:${field}"]`);
            (target?.matches('input, select, textarea') ? target : target?.querySelector<HTMLElement>('input, select') ?? target)?.focus();
            target?.scrollIntoView({behavior: 'smooth', block: 'center'});
        });
    }
}
